<template>
	<div
		@click="$emit('click', $event)"
		:style="`${formStyle};${brandStyle}`"
		:id="item.id"
		v-if="isReady"
		:key="refreshKey"
		:class="['agreement-form-embed', 'brand-agreement-surface', { 'completed-form-embed': completed, 'mx-brand-form-surface': branding }, ...brandClasses]"
	>
		<div class="mx-brand-form" v-if="item.discoveryTemplate && schema.length">
			<div v-for="entry in renderedItems" v-show="isItemVisible(entry.item)" :key="entry.item.id" :id="entry.item.id">
				<item-renderer
					ref="item"
					:index="entry.index"
					:item="entry.item"
					:styles="styles"
					:font="font"
					:button-props="buttonProps"
					:disabled="!clientMode || agreement.fullyExecuted"
					:current-page="entry.page + 1"
					:page-count="pages.length"
					:account-logo="accountLogo"
					:active="false"
					:results="results"
					:known-data="knownData"
					@next-page="nextPage()"
					@previous-page="previousPage()"
					@input="handleInput($event)"
				></item-renderer>
			</div>
			<div v-if="isLastPage && !printing">
				<div class="row-format gap-2 pt-2 px-2" :style="`width: 100%; justify-content: ${justification}`">
					<v-btn
						v-if="pages.length > 1"
						class="elevation-0"
						:color="styles.buttonColor"
						:style="
							`font-size: var(--form-font-size) !important; ${buttonProps.block ? 'width: 49%!important;' : ''}`
						"
						:x-small="buttonProps.xSmall"
						:small="buttonProps.small"
						:large="buttonProps.large"
						:x-large="buttonProps.xLarge"
						@click="previousPage"
					>
						<v-icon class="mr-1" :color="styles.buttonTextColor">{{ styles.prevPageIcon }}</v-icon>
						<span :style="`font-family: ${font}; ; text-transform: none; color: ${styles.buttonTextColor}`">{{
							styles.prevPageText
						}}</span>
					</v-btn>
				</div>
			</div>
		</div>
		<div
			v-else-if="form && schema.length === 0"
			class="row-format centered"
			style="height:100%; border: 1px solid var(--v-gray_20-base); min-height: 100px"
		>
			<div class="font-gray_60 row-format align-center">
				<v-icon class="material-symbols-outlined" color="gray_60">warning</v-icon>
				<div>Selected form has no content</div>
			</div>
		</div>
		<div v-else class="row-format centered" style="height:100%; border: 1px solid var(--v-gray_20-base); min-height: 100px">
			<div class="font-gray_60 row-format align-center">
				<v-icon class="material-symbols-outlined" color="gray_60">description</v-icon>
				<div>Please select an embedded form template</div>
			</div>
		</div>
	</div>
</template>

<script>
	import ItemRenderer from '@/modules/forms/v2/schema/ItemRenderer';
	import ResponsiveMixin from './ResponsiveMixin';
	import RendererMixin from '@/modules/forms/v2/schema/RendererMixin';
	import { agreementBranding, formBrandClasses, formBrandStyle } from '@moxie/branding';

	export default {
		name: 'FormEmbed',

		props: ['agreement', 'item', 'accountLogo', 'editMode', 'clientMode', 'signer', 'isMobile', 'pageWidth'],

		components: { ItemRenderer },

		mixins: [ResponsiveMixin, RendererMixin],
		// Item ids are UUIDs, which may start with a digit and so aren't valid `#id` selectors.
		provide() {
			return { formBranding: () => this.branding, formBrandTarget: () => this.branding ? `[id="${this.item.id}"]` : false };
		},

		data: function() {
			return {
				currentPage: 0,
				results: [],
				isReady: false,
				refreshKey: 0,
				completed: false,
				knownData: null,
			};
		},

		mounted() {
			this.setKnownData();
			if (this.item.formData) {
				this.formatPreviousAnswers();
			} else {
				this.loadPartialForm();
			}
		},

		beforeDestroy() {},

		methods: {
			setKnownData: function() {
				let signer = this.signer && this.signer.signerType !== 'USER' ? this.signer : null;

				this.knownData = {
					firstName: signer?.firstName,
					lastName: signer?.lastName,
					email: signer?.email,
					phone: this.agreement?.client?.phone,
					businessName: this.agreement?.client?.name,
					website: this.agreement?.client?.website,
					address1: this.agreement?.client?.address1,
					address2: this.agreement?.client?.address2,
					city: this.agreement?.client?.city,
					locality: this.agreement?.client?.locality,
					postal: this.agreement?.client?.postal,
					country: this.agreement?.client?.country,
				};
			},

			loadPartialForm: function() {
				try {
					let partialResult = localStorage.getItem(this.savedFormKey);
					if (partialResult) {
						this.results.push(...JSON.parse(partialResult));
						if (this.publicMode) this.results.forEach((result) => this.applyToClient(result));
					}
				} catch (err) {
					console.log(err);
				}
				this.isReady = true;
			},

			finalize: function() {
				return new Promise((resolve, reject) => {
					if (this.schema.length === 0) {
						resolve();
						return;
					}

					if (!this.doValidation() || this.currentPage < this.pages.length - 1) {
						document.getElementById(this.item.id).scrollIntoView({ block: 'start', behavior: 'smooth' });
						document.getElementById(this.item.id).classList.add('red-border');
						reject();
						return false;
					} else {
						document.getElementById(this.item.id).classList.remove('red-border');
					}

					this.results.sort((a, b) => a.index.toString().localeCompare(b.index.toString()));

					let formResult = {};
					formResult.answers = [];
					let files = [];

					for (let i = 0; i < this.results.length; i++) {
						let item = this.results[i].item;
						let value = this.results[i].value;

						if (item.schemaMapping) {
							formResult[item.schemaMapping] = Array.isArray(value) ? value.join(', ') : value;
							//continue;
						}

						let answer;
						if (item.type === 'FileInput') {
							files.push(...value);
							answer = JSON.stringify(value.map((f) => f.name));
						} else {
							answer = Array.isArray(value) ? value.join(', ') : value;
						}

						formResult.answers.push({
							id: item.id,
							fieldKey: item.fieldName,
							fieldType: item.type,
							question: item.question,
							answer: answer,
						});
					}

					this.item.formData = formResult;
					if (this.agreement.fileUploads) {
						this.agreement.fileUploads.push(...files);
					} else {
						this.agreement.fileUploads = [...files];
					}
					resolve();
				});
			},

			doValidation: function() {
				let invalidCount = 0;
				for (let i = 0; i < this.$refs.item.length; i++) {
					if (!this.$refs.item[i].validate()) {
						invalidCount++;
					}
				}
				return !invalidCount;
			},

			nextPage: function() {
				if (!this.doValidation()) {
					this.errorText = 'Please correct the errors in your form';
					this.showError = true;
					return;
				}
				this.currentPage++;
				this.scrollToFormTop();
			},

			previousPage: function() {
				this.currentPage--;
				this.scrollToFormTop();
			},

			// A shorter next page would otherwise leave the reader scrolled past the form.
			scrollToFormTop: function() {
				this.$nextTick(() => {
					const form = document.getElementById(this.item.id);
					if (form) form.scrollIntoView({ block: 'start', behavior: 'smooth' });
				});
			},

			handleInput: function(event) {
				let ix = this.results.findIndex((r) => r.item.id === event.item.id);
				if (ix > -1) {
					this.results.splice(ix, 1, event);
				} else {
					this.results.push(event);
				}

				try {
					localStorage.setItem(this.savedFormKey, JSON.stringify(this.results));
				} catch (err) {
					console.log(err);
				}

				this.applyToClient(event);
			},

			// Mapped answers update agreement.client so client tokens read them. A public link's
			// form is the visitor's identity, so it always does (the client is created from it on
			// Finish); otherwise only when the block merges client data.
			applyToClient: function(event) {
				if (this.publicMode) this.syncPublicIdentity();

				if ((this.item?.mergeClientData || this.publicMode) && event.value) {
					if (event.item?.schemaMapping) {
						if (event.item.schemaMapping === 'businessName') {
							if (!this.publicMode) this.agreement.client.name = event.value;
						} else {
							this.agreement.client[event.item.schemaMapping] = event.value;
						}
					} else {
						let key = event.item.fieldName;
						let customField = this.clientFields.find((f) => f.mappingKey === key);
						if (customField) {
							let customValue = {
								fieldId: customField.id,
								fieldName: customField.name,
								mappingKey: customField.mappingKey,
								type: customField.type,
								value: event.value,
								valueAsString: event.value.toString(),
							};
							if (!this.agreement.client.customValues) this.$set(this.agreement.client, 'customValues', []);
							let ix = this.agreement.client.customValues.findIndex((f) => f.fieldId === customField.id);
							if (ix > -1) {
								this.agreement.client.customValues.splice(ix, 1, customValue);
							} else {
								this.agreement.client.customValues.push(customValue);
							}
						}
					}
				}
			},

			// A public link's visitor is who the form says they are: the client is named as the
			// server names a new one (the business, or the person when blank), and the visitor's
			// signer takes their name and email so the signature line and sign dialog show them.
			syncPublicIdentity: function() {
				const answer = (mapping) => {
					const result = this.results.find((r) => r.item && r.item.schemaMapping === mapping);
					return result && result.value ? String(result.value).trim() : '';
				};
				const fullName = [answer('firstName'), answer('lastName')].filter(Boolean).join(' ');
				this.agreement.client.name = answer('businessName') || fullName || null;

				if (!this.signer || this.signer.signerType !== 'CLIENT') return;
				const signers = [this.signer, ...(this.agreement.signers || []).filter((s) => s.id === this.signer.id && s !== this.signer)];
				for (const signer of signers) {
					for (const mapping of ['firstName', 'lastName', 'email']) {
						if (answer(mapping)) this.$set(signer, mapping, answer(mapping));
					}
				}
			},

			formatPreviousAnswers: function() {
				let items = this.item.discoveryTemplate.schemaV2.filter(this.isInputType);
				let formData = this.item.formData;
				let index = 0;
				let currentPage = 0;

				for (let i = 0; i < items.length; i++) {
					let item = items[i];

					if (item.type === 'NewPage') {
						index = 0;
						currentPage = currentPage + 1;
						continue;
					}

					if (item.type === 'Container') {
						for (let j = 0; j < item.columns.length; j++) {
							let columnSchemaItems = item.columns[j].items.filter(this.isInputType);
							for (let k = 0; k < columnSchemaItems.length; k++) {
								let resultIndex = index++ + (currentPage + 1) * 1000;
								this.processItem(formData, columnSchemaItems[k], resultIndex);
							}
						}
					} else {
						let resultIndex = index++ + (currentPage + 1) * 1000;
						this.processItem(formData, item, resultIndex);
					}
				}
				this.isReady = true;
				this.completed = true;
				this.$nextTick(() => this.refreshKey++);
			},

			processItem(formData, item, resultIndex) {
				let value = null;

				let answer = formData.answers.find((a) => a.id === item.id);
				if (answer) {
					if (item.type === 'Checkbox') {
						value = [];
						if (answer.answer) {
							answer.answer.split(',').forEach((a) => value.push(a.trim()));
						}
					} else if (item.type === 'FileInput') {
						answer.answer = JSON.parse(answer.answer);
						value = [];
						for (let i = 0; i < answer.answer.length; i++) {
							let fileName = answer.answer[i];
							let s3File = this.item.files.find((f) => f.fileName === fileName);
							value.push(s3File);
						}
					} else {
						value = answer.answer;
					}
				} else if (item.schemaMapping) {
					value = formData[item.schemaMapping];
				}

				this.results.push({
					item: item,
					index: resultIndex,
					value: value,
				});
			},

			isInputType(item) {
				let type = item.type;
				return (
					type === 'FileInput' ||
					type === 'Checkbox' ||
					type === 'DateInput' ||
					type === 'Radio' ||
					type === 'Select' ||
					type === 'TextArea' ||
					type === 'TextInput' ||
					type === 'Container' ||
					type === 'NewPage'
				);
			},
		},

		computed: {
			publicMode() {
				return this.$store.state.publicAgreement === true;
			},
			branding() { return agreementBranding(this.agreement); },
			brandStyle() { return Object.entries(this.branding ? formBrandStyle(this.branding) : {}).map(([key, value]) => `${key}:${value}`).join(';'); },
			brandClasses() { return formBrandClasses(this.branding); },
			clientFields: function() {
				if (this.$store.state.clientFields && this.$store.state.clientFields.fields) {
					return this.$store.state.clientFields.fields;
				} else {
					return [];
				}
			},

			savedFormKey: function() {
				return `form-${this.agreement.id}-${this.item.attachedFormId}`;
			},

			formStyle: function() {
				let style = `margin: ${this.item.padding}px; padding-top: ${this.item.pt}px; padding-bottom: ${
					this.item.pb
				}px; padding-left: ${this.padding(this.item.pl)}px; padding-right: ${this.padding(this.item.pr)}px;`;

				if (this.item.borderColor) {
					style = style + `border-color: ${this.item.borderColor}; `;
				}
				if (this.item.borderWidth) {
					style = style + `border-style: solid; border-width: ${this.item.borderWidth}px; `;
				}
				if (this.item.borderRadius) {
					style = style + `border-radius: ${this.item.borderRadius}px; `;
				}
				return style;
			},

			printing: function() {
				return this.$store.state.documentPrinting;
			},

			// The current page on screen; every page in sequence when printing, so no answers
			// are hidden behind page navigation. Indexes match formatPreviousAnswers.
			renderedItems: function() {
				const pageEntries = (page) => this.pages[page].map((item, index) => ({ item, index: index + (page + 1) * 1000, page }));
				if (!this.printing) return pageEntries(this.currentPage);
				return this.pages.flatMap((_, page) => pageEntries(page)).filter((entry) => entry.item.type !== 'NewPage');
			},

			isLastPage: function() {
				if (this.currentPage === this.pages.length - 1) {
					return true;
				} else {
					return false;
				}
			},

			pages: function() {
				let pages = [[]];
				for (let i = 0; i < this.schema.length; i++) {
					pages[pages.length - 1].push(this.schema[i]);
					if (this.schema[i].type === 'NewPage') {
						pages.push([]);
					}
				}
				return pages;
			},

			form: function() {
				return this.item.discoveryTemplate;
			},

			styles: function() {
				return this.form ? this.form.formSettingsV2 : {};
			},

			schema: function() {
				return this.form ? this.form.schemaV2 : [];
			},

			font: function() {
				return this.form ? this.form.font : null;
			},

			buttonProps: function() {
				let result = {
					xSmall: this.styles.buttonSize === 'X-Small',
					small: this.styles.buttonSize === 'Small',
					large: this.styles.buttonSize === 'Large',
					xLarge: this.styles.buttonSize === 'X-Large',
					block: this.styles.buttonStyle === 'Block',
				};

				return result;
			},

			justification: function() {
				if (this.styles.submitAlignment === 'JUSTIFIED') {
					return 'space-between';
				} else if (this.styles.submitAlignment === 'LEFT') {
					return 'flex-start';
				} else if (this.styles.submitAlignment === 'RIGHT') {
					return 'flex-end';
				} else {
					return 'center';
				}
			},
		},
	};
</script>

<style lang="scss">
	.agreement-form-embed.mx-brand-form-surface { position: relative; }
	.completed-form-embed:not(.mx-brand-form-surface) {
		.v-input {
			textarea {
				color: var(--v-black-base) !important;
			}

			input {
				color: var(--v-black-base) !important;
			}

			.v-select__selection--disabled {
				color: var(--v-black-base) !important;
			}

			.v-label--is-disabled {
				color: var(--v-black-base) !important;
			}
		}
	}
</style>
